import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import { fulfillmentTokens, partOrders } from '../../services/dataClient';

const MAX_ATTEMPTS = 3;
const TOKEN_TTL_MS = 30 * 60 * 1000; // 30 minutes

// ── Shared layout wrapper ─────────────────────────────────────────────────────
function PageShell({ children }) {
  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#F8FAFC',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      pt: { xs: 4, sm: 6 },
      px: 2,
      pb: 6,
    }}>
      {/* Header bar */}
      <Box sx={{ width: '100%', maxWidth: 420,
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 3,
      }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '8px',
          backgroundColor: '#009688',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem' }}>C</Typography>
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          CloudCover Logistics
        </Typography>
      </Box>

      {/* Card */}
      <Box sx={{ width: '100%', maxWidth: 420,
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '1px solid #E3E8EF',
        boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
        overflow: 'hidden',
      }}>
        {children}
      </Box>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PortalVerifyPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  // Resolve entry — priority order:
  //   1. ?d= URL param  (cross-device: iPhone scans QR → decodes from URL, no server needed)
  //   2. localStorage   (same-browser: coordinator opens link in same browser as portal)
  //   3. in-memory Map  (same-tab fallback)
  // Returns null if token is missing, not found, already used, or older than TOKEN_TTL_MS.
  const entry = (() => {
    if (!token) return null;

    // 1. URL-encoded payload (primary — works on any device)
    const dParam = searchParams.get('d');
    if (dParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(dParam)))));
        if (decoded.createdAt && Date.now() - decoded.createdAt > TOKEN_TTL_MS) return null;
        return decoded;
      } catch { /* fall through */ }
    }

    // 2. localStorage (same-browser tabs)
    try {
      const raw = localStorage.getItem(`coverit:ft:${token}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.createdAt && Date.now() - parsed.createdAt > TOKEN_TTL_MS) {
          try { localStorage.removeItem(`coverit:ft:${token}`); } catch { /* ignore */ }
          fulfillmentTokens.delete(token);
          return null;
        }
        return parsed;
      }
    } catch { /* ignore */ }

    // 3. In-memory Map (same-tab)
    const mem = fulfillmentTokens.get(token) ?? null;
    if (mem?.createdAt && Date.now() - mem.createdAt > TOKEN_TTL_MS) {
      fulfillmentTokens.delete(token);
      return null;
    }
    return mem;
  })();

  const [step, setStep]             = useState('pin'); // 'pin' | 'photo' | 'success' | 'locked'
  const [pinInput, setPinInput]     = useState('');
  const [pinError, setPinError]     = useState('');
  const [attempts, setAttempts]     = useState(0);
  const [shake, setShake]           = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);

  // ── Countdown timer ─────────────────────────────────────────────────────────
  const [timeLeft, setTimeLeft] = useState(() =>
    entry?.createdAt ? Math.max(0, TOKEN_TTL_MS - (Date.now() - entry.createdAt)) : 0,
  );

  useEffect(() => {
    if (!entry || step === 'success' || step === 'locked' || timeLeft <= 0) return;
    const id = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [entry, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Invalid / expired token ─────────────────────────────────────────────────
  if (!entry) {
    return (
      <PageShell>
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: 'var(--grey-300)', mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', mb: 1 }}>
            Link Expired or Invalid
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', lineHeight: 1.6 }}>
            This QR code has expired or is no longer valid. QR codes are valid for 30 minutes
            .Please ask your coordinator to regenerate the fulfillment link.
          </Typography>
        </Box>
      </PageShell>
    );
  }

  const order = partOrders.find((o) => o.id === entry.orderId);

  // ── PIN entry ───────────────────────────────────────────────────────────────
  const handlePinSubmit = () => {
    if (pinInput.trim() === entry.pin) {
      setStep('photo');
      setPinError('');
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      // Shake animation
      setShake(true);
      setTimeout(() => setShake(false), 500);
      if (newAttempts >= MAX_ATTEMPTS) {
        setStep('locked');
      } else {
        const remaining = MAX_ATTEMPTS - newAttempts;
        setPinError(`Incorrect code. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
        setPinInput('');
      }
    }
  };

  // ── Photo selection ─────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoDataUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handlePhotoSubmit = () => {
    const now = new Date().toISOString();
    // Write to localStorage — the modal polls this key every 1.5 s across tabs
    try {
      const updated = { ...entry, photoDataUrl, uploadedAt: now };
      localStorage.setItem(`coverit:ft:${token}`, JSON.stringify(updated));
      // Also sync to in-memory Map (same-tab fallback)
      fulfillmentTokens.set(token, updated);
    } catch { /* ignore storage errors */ }
    // Also write directly to the partOrder if accessible in this session
    if (order) {
      order.fulfillmentPhoto = { dataUrl: photoDataUrl, uploadedAt: now, token };
    }
    setStep('success');
  };

  // ── Countdown formatting ─────────────────────────────────────────────────────
  const fmtTimeLeft = () => {
    const totalSecs = Math.floor(timeLeft / 1000);
    const m = Math.floor(totalSecs / 60);
    const s = totalSecs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };
  const isExpired = timeLeft <= 0;
  const isWarning = timeLeft > 0 && timeLeft <= 5 * 60 * 1000; // last 5 minutes

  // ── Ticket info block (shared across steps) ─────────────────────────────────
  const TicketInfo = () => (
    <Box sx={{
      px: 3, py: 2,
      borderBottom: '1px solid #EEF2F6',
      backgroundColor: '#F8FAFC',
    }}>
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
        Ticket
      </Typography>
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--grey-900)' }}>
        {entry.ticketNumber}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', mt: '2px' }}>
        {entry.partNumber} — {entry.partDescription}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
        Serial:{' '}
        <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--grey-700)' }}>
          {entry.serialNumber}
        </Box>
      </Typography>
      {/* Countdown */}
      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: '0.75rem',
          fontWeight: 600,
          color: isExpired ? '#C62828' : isWarning ? '#E65100' : 'var(--grey-400)',
        }}>
          {isExpired ? 'Link expired' : `Expires in ${fmtTimeLeft()}`}
        </Typography>
      </Box>
    </Box>
  );

  // ── Render: Locked ──────────────────────────────────────────────────────────
  if (step === 'locked') {
    return (
      <PageShell>
        <TicketInfo />
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 44, color: '#EF5350', mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', mb: 1 }}>
            Too Many Attempts
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', lineHeight: 1.6 }}>
            This link has been locked after {MAX_ATTEMPTS} incorrect attempts
            .Please contact your coordinator to generate a new fulfillment link.
          </Typography>
        </Box>
      </PageShell>
    );
  }

  // ── Render: Success ─────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <PageShell>
        <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%',
            backgroundColor: '#E8F5E9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2.5,
          }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: '#43A047' }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--grey-900)', mb: 1 }}>
            Part Confirmed!
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', lineHeight: 1.6 }}>
            Your photo has been submitted for{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'var(--grey-700)' }}>
              {entry.ticketNumber}
            </Box>
            .
            <br />
            You can close this window.
          </Typography>
          {photoDataUrl && (
            <Box sx={{ mt: 3, borderRadius: '10px', overflow: 'hidden',
              border: '2px solid #C8E6C9',
              maxWidth: 280, mx: 'auto',
            }}>
              <Box
                component="img"
                src={photoDataUrl}
                alt="Submitted part"
                sx={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 200 }}
              />
            </Box>
          )}
        </Box>
      </PageShell>
    );
  }

  // ── Render: Photo upload ────────────────────────────────────────────────────
  if (step === 'photo') {
    return (
      <PageShell>
        <TicketInfo />
        {isExpired ? (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 44, color: 'var(--grey-300)', mb: 2 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', mb: 1 }}>
              Link Expired
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', lineHeight: 1.6 }}>
              This QR code is no longer valid. Please ask your coordinator to generate a new fulfillment link.
            </Typography>
          </Box>
        ) : (
        <Box sx={{ px: 3, pt: 3, pb: 4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', mb: 0.5 }}>
            Upload Part Photo
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mb: 2.5, lineHeight: 1.55 }}>
            Take a photo of the part, ideally showing the serial number label clearly.
          </Typography>

          {/* Camera button / preview */}
          <Box
            component="label"
            htmlFor="part-photo-input"
            sx={{ display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              width: '100%',
              minHeight: photoDataUrl ? 'auto' : 160,
              borderRadius: '12px',
              border: `2px dashed ${photoDataUrl ? '#009688' : 'var(--grey-300)'}`,
              backgroundColor: photoDataUrl ? '#E0F2F1' : '#FAFAFA',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': {
                borderColor: '#009688',
                backgroundColor: '#F0FBF9',
              },
              overflow: 'hidden',
              mb: 2.5,
            }}
          >
            {photoDataUrl ? (
              <Box
                component="img"
                src={photoDataUrl}
                alt="Part preview"
                sx={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 240 }}
              />
            ) : (
              <>
                <CameraAltIcon sx={{ fontSize: 36, color: 'var(--grey-400)' }} />
                <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', textAlign: 'center' }}>
                  Tap to take a photo or choose from gallery
                </Typography>
              </>
            )}
          </Box>

          {/* Hidden file input — capture="environment" opens rear camera on mobile */}
          <Box
            component="input"
            id="part-photo-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileChange}
            sx={{ display: 'none' }}
          />

          {photoDataUrl && (
            <Typography
              component="label"
              htmlFor="part-photo-input"
              sx={{ display: 'block', textAlign: 'center',
                fontSize: '0.8125rem', color: '#009688', cursor: 'pointer',
                mb: 2.5,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Retake photo
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            disabled={!photoDataUrl}
            onClick={handlePhotoSubmit}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              py: 1.5,
              borderRadius: '10px',
              boxShadow: 'none',
            }}
          >
            Submit Photo
          </Button>
        </Box>
        )}
      </PageShell>
    );
  }

  // ── Render: PIN entry (default) ─────────────────────────────────────────────
  return (
    <PageShell>
      <TicketInfo />
      {isExpired ? (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 44, color: 'var(--grey-300)', mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', mb: 1 }}>
            Link Expired
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', lineHeight: 1.6 }}>
            This QR code is no longer valid. Please ask your coordinator to generate a new fulfillment link.
          </Typography>
        </Box>
      ) : (
      <Box sx={{ px: 3, pt: 3, pb: 4 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', mb: 0.5 }}>
          Enter Confirmation Code
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mb: 3, lineHeight: 1.55 }}>
          Enter the 4-digit code shown on the fulfillment screen to continue.
        </Typography>

        <TextField
          fullWidth
          placeholder="- - - -"
          value={pinInput}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 4);
            setPinInput(val);
            if (pinError) setPinError('');
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && pinInput.length === 4) handlePinSubmit(); }}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4, style: { textAlign: 'center', fontSize: '1.75rem', fontWeight: 700, letterSpacing: '0.4em', fontFamily: 'monospace' } }}
          error={Boolean(pinError)}
          helperText={pinError}
          sx={{ mb: 2.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              '& fieldset': { borderColor: shake ? '#EF5350' : 'var(--grey-200)' },
              '&.Mui-focused fieldset': { borderColor: '#009688' },
              '&.Mui-error fieldset': { borderColor: '#EF5350' },
              animation: shake ? 'shakeInput 0.4s ease' : 'none',
              '@keyframes shakeInput': {
                '0%, 100%': { transform: 'translateX(0)' },
                '20%':      { transform: 'translateX(-8px)' },
                '40%':      { transform: 'translateX(8px)' },
                '60%':      { transform: 'translateX(-6px)' },
                '80%':      { transform: 'translateX(6px)' },
              },
            },
            '& .MuiFormHelperText-root': { textAlign: 'center', fontSize: '0.8125rem' },
          }}
        />

        <Button
          variant="contained"
          fullWidth
          disabled={pinInput.length !== 4}
          onClick={handlePinSubmit}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1rem',
            py: 1.5,
            borderRadius: '10px',
            boxShadow: 'none',
          }}
        >
          Continue
        </Button>
      </Box>
      )}
    </PageShell>
  );
}
